<script setup lang="ts">
import { computed, inject, nextTick, onMounted, watch, type Ref, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useForm } from 'vee-validate';
import { useMutation, useQuery } from '@tanstack/vue-query';
import * as yup from 'yup';
import type { AxiosError } from 'axios';
import * as rutHelpers from 'rut-helpers';
import { useAddressGeocoding, type GeocodeResult } from '@/composables/useAddressGeocoding';
import { caregiversApi, type Caregiver, type CaregiverForm } from '@/api/caregivers';
import { countriesApi } from '@/api/countries';
import { communesApi } from '@/api/communes';
import { useCareerSkills } from '@/composables/useCareerSkills';
import { type SerializedUser } from '@/api/users';
import GoBackButton from '@/components/go-back-button.vue';
import GroupMembershipPicker from '@/components/groups/group-membership-picker.vue';
import { useGroupMembershipPicker } from '@/composables/useGroupMembershipPicker';
import { isNumber } from '@/utils/number-functions';
import { useAccessibleCareers } from '@/composables/useAccessibleCareers';
import CaregiverAuditableFields from './caregiver-auditable-fields.vue';

const { t } = useI18n();

const currentUser = inject<Ref<SerializedUser>>('currentUser');

const { accessibleCareers } = useAccessibleCareers();

interface Props {
  caregiver: CaregiverForm | undefined;
  mode: 'new' | 'edit';
}

const props = defineProps<Props>();

const MIN_BIRTH_YEAR = 1900;

// An ERP-managed caregiver's name comes as the ERP has it, possibly a single word.
function identityField() {
  return props.caregiver?.erpManaged ?
    yup.string().nullable() :
    yup.string().required(t('caregiverForm.fieldRequired'));
}

const validationSchema = yup.object({
  firstName: identityField(),
  lastName: identityField(),
  email: yup.string().nullable().when([], {
    is: () => currentUser?.value?.companyAuditable,
    then: (schema) => schema.required(t('caregiverForm.fieldRequired')),
    otherwise: (schema) => schema,
  }),
  dni: yup.string().nullable()
    .when([], {
      is: () => currentUser?.value?.companyCaregiverDniRequired,
      then: (schema) => schema.required(t('caregiverForm.fieldRequired')),
      otherwise: (schema) => schema,
    })
    .test('valid-dni', t('caregiverForm.invalidDni'), (value) => (value ? rutHelpers.rutValidate(value) : true)),
  dateOfBirth: yup.date()
    .nullable()
    .transform((value) => (value === '' || Number.isNaN(Date.parse(value)) ? null : value))
    .test('birthdate-range', t('patientForm.birthdateRequired'), (value) => {
      if (value === null || value === undefined) return true;

      return value >= new Date(MIN_BIRTH_YEAR, 0, 1) && value <= new Date();
    }),
  endsOn: yup.date()
    .nullable()
    .transform((value) => (value === '' || Number.isNaN(Date.parse(value)) ? null : value))
    .test('ends-after-starts', t('caregiverForm.endsOnMustBeAfterStartsOn'), function (value) {
      if (!value) return true;
      const { startsOn } = this.parent;
      if (!startsOn) return true;

      return new Date(value) >= new Date(startsOn);
    }),
  address: yup.string().nullable()
    .test('has-coordinates', t('caregiverForm.addressRequiresLocation'), function (value) {
      if (!value) return true;
      const { latitude, longitude } = this.parent;

      return !!(latitude && longitude);
    }),
  compensationType: yup.string().when('agreedOvertimeCompensation', {
    is: true,
    then: (schema) => schema.required(t('caregiverForm.fieldRequired')),
    otherwise: (schema) => schema.nullable(),
  }),
});

const DEFAULT_EMPLOYMENT_CONTRACT_TYPE = 'tax_bill';

const initialValues = props.caregiver ? {
  ...props.caregiver,
  employmentContractType: props.caregiver.employmentContractType ?? DEFAULT_EMPLOYMENT_CONTRACT_TYPE,
} : {
  firstName: undefined,
  lastName: undefined,
  email: undefined,
  corporateEmail: undefined,
  sendAppAccess: false,
  phoneNumber: undefined,
  countryId: undefined,
  address: undefined,
  latitude: undefined,
  longitude: undefined,
  dateOfBirth: undefined,
  description: undefined,
  dni: undefined,
  sex: undefined,
  career: undefined,
  employmentContractType: DEFAULT_EMPLOYMENT_CONTRACT_TYPE,
  workdayType: undefined,
  agreedOvertimeCompensation: false,
  compensationType: undefined,
  flexibleWorkingHours: false,
  exceptionalWorkdayResolution: undefined,
  exceptionalWorkdayDate: undefined,
  exceptionalWorkdayText: undefined,
  officeId: undefined,
  serviceCompanyId: undefined,
  startsOn: undefined,
  endsOn: undefined,
  skillIds: [],
};

const { handleSubmit, values, errors, defineField } = useForm<CaregiverForm>({ validationSchema, initialValues });

const [idCode, idCodeAttrs] = defineField('idCode');
const [firstName, firstNameAttrs] = defineField('firstName');
const [lastName, lastNameAttrs] = defineField('lastName');
const [email, emailAttrs] = defineField('email');
const [corporateEmail, corporateEmailAttrs] = defineField('corporateEmail');
const [sendAppAccess, sendAppAccessAttrs] = defineField('sendAppAccess');
const [phoneNumber, phoneNumberAttrs] = defineField('phoneNumber');
const [countryId, countryIdAttrs] = defineField('countryId');
const [address, addressAttrs] = defineField('address');
const [latitude, ,] = defineField('latitude');
const [longitude, ,] = defineField('longitude');
const [communeId, communeIdAttrs] = defineField('communeId');
const [dateOfBirth, dateOfBirthAttrs] = defineField('dateOfBirth');
const [dni, dniAttrs] = defineField('dni');
const [passportNumber, passportNumberAttrs] = defineField('passportNumber');
const [sex, sexAttrs] = defineField('sex');
const [description, descriptionAttrs] = defineField('description');
const [career, careerAttrs] = defineField('career');
const [skillIds] = defineField('skillIds');
const [comments, commentsAttrs] = defineField('comments');
const [employmentContractType] = defineField('employmentContractType');
const [workdayType] = defineField('workdayType');
const [agreedOvertimeCompensation] = defineField('agreedOvertimeCompensation');
const [compensationType] = defineField('compensationType');
const [flexibleWorkingHours] = defineField('flexibleWorkingHours');
const [exceptionalWorkdayResolution] = defineField('exceptionalWorkdayResolution');
const [exceptionalWorkdayDate] = defineField('exceptionalWorkdayDate');
const [exceptionalWorkdayText] = defineField('exceptionalWorkdayText');
const [officeId] = defineField('officeId');
const [serviceCompanyId] = defineField('serviceCompanyId');
const [startsOn, startsOnAttrs] = defineField('startsOn');
const [endsOn, endsOnAttrs] = defineField('endsOn');

watch(dni, (value) => {
  dni.value = rutHelpers.rutFormat(value);
}, { immediate: true });

const { data: countries } = useQuery({
  queryKey: ['countries'],
  queryFn: () => countriesApi.index(),
});

const { data: communes } = useQuery({
  queryKey: ['communes'],
  queryFn: () => communesApi.index(),
});

// Skills are a care-planning feature; only load and show them when enabled.
const carePlanningEnabled = computed(() => !!currentUser?.value?.companyCarePlanningEnabled);
// Name, RUT and career of a caregiver that comes from the company's ERP are edited there.
const identityLocked = computed(() => !!props.caregiver?.erpManaged);
const { careerSkills } = useCareerSkills(career, skillIds, [], carePlanningEnabled);

const submitButtonDisabled = ref(false);

const {
  showGroupPicker,
  mustPickGroup,
  selectedGroupIds,
  groupPickError,
  groupPickerHint,
  groupItems,
  validate: validateGroupPick,
} = useGroupMembershipPicker(
  props.mode, 'caregiver', (props.caregiver as Partial<Caregiver>)?.groupIds ?? [],
);

const { attachAutocomplete, openMapPicker } = useAddressGeocoding();
const showMap = ref(false);

function applyGeocode(result: GeocodeResult): void {
  if (result.address) address.value = result.address;
  if (result.latitude) latitude.value = result.latitude;
  if (result.longitude) longitude.value = result.longitude;
}

onMounted(() => {
  attachAutocomplete('caregiver-address-input', applyGeocode);
});

async function openMap() {
  showMap.value = true;
  await nextTick();
  const position = latitude.value && longitude.value ?
    { lat: parseFloat(String(latitude.value)), lng: parseFloat(String(longitude.value)) } :
    null;
  await openMapPicker('caregiver-map', position, applyGeocode);
}

function onAddressInput() {
  latitude.value = '';
  longitude.value = '';
}

function sanitizedValues() {
  return { ...values, email: values.email || null };
}

const { mutate: createCaregiver, isError: isCreateError, error: createError } = useMutation<unknown, AxiosError>(
  {
    mutationFn: () => caregiversApi.create(
      sanitizedValues(),
      showGroupPicker ? selectedGroupIds.value : undefined,
    ),
    onSuccess: () => { window.location.href = '/caregivers'; },
    onError: () => { submitButtonDisabled.value = false; },
  },
);

const { mutate: updateCaregiver, isError: isUpdateError, error: updateError } = useMutation<unknown, AxiosError>(
  {
    mutationFn: () => caregiversApi.update(
      { ...sanitizedValues(), id: props.caregiver?.id },
      showGroupPicker ? selectedGroupIds.value : undefined,
    ),
    onSuccess: () => { window.location.href = `/caregivers/${props.caregiver?.id}`; },
    onError: () => { submitButtonDisabled.value = false; },
  },
);

const onSubmit = handleSubmit(() => {
  if (!validateGroupPick()) return;

  submitButtonDisabled.value = true;
  if (props.mode === 'edit') {
    updateCaregiver();
  } else {
    createCaregiver();
  }
});

function errorMessage(error: AxiosError) {
  const responseData = error.response?.data as {
    type?: string; errors?: Record<string, string[]>;
  } | undefined;
  if (responseData?.type === 'ActiveRecord::RecordNotUnique') {
    return t('caregiverForm.recordNotUnique');
  }
  if (responseData?.errors) {
    return Object.values(responseData.errors).flat().join('. ');
  }

  return t('userSession.defaultError');
}
</script>

<template>
  <v-container
    class="mx-auto py-4"
    style="max-width: 880px;"
  >
    <go-back-button />
    <h2 class="text-h5 font-weight-medium my-4">
      {{ mode === 'new' ? t('caregiverForm.createTitle') : t('caregiverForm.updateTitle') }}
    </h2>
    <v-form
      class="w-100 ma-0"
      @submit.prevent="onSubmit"
    >
      <!-- Personal information -->
      <v-card class="mb-6">
        <v-card-item>
          <v-card-title class="text-subtitle-1 font-weight-bold">
            {{ t('caregiverForm.sectionPersonalInfo') }}
          </v-card-title>
        </v-card-item>
        <v-card-text class="pt-4">
          <v-alert
            v-if="identityLocked"
            type="info"
            variant="tonal"
            density="compact"
            class="mb-4"
            text="El nombre, RUT y especialidad de este profesional vienen del ERP de la empresa y se editan allá."
          />
          <v-row>
            <v-col
              v-if="currentUser?.companyUsesIdCode"
              cols="12"
              sm="6"
            >
              <v-text-field
                v-model.number="idCode"
                v-bind="idCodeAttrs"
                type="number"
                variant="outlined"
                :label="`${t('caregiver.idCode')}`"
                :error-messages="errors.idCode"
                hide-details="auto"
                @wheel="$event.target.blur()"
                @keypress="isNumber($event)"
              />
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-text-field
                v-model="firstName"
                v-bind="firstNameAttrs"
                :disabled="identityLocked"
                variant="outlined"
                :label="`${t('caregiver.firstName')} (requerido)`"
                :error-messages="errors.firstName"
                hide-details="auto"
              />
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-text-field
                v-model="lastName"
                v-bind="lastNameAttrs"
                :disabled="identityLocked"
                variant="outlined"
                :label="`${t('caregiver.lastName')} (requerido)`"
                :error-messages="errors.lastName"
                hide-details="auto"
              />
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-text-field
                v-model="dni"
                v-bind="dniAttrs"
                :disabled="identityLocked"
                variant="outlined"
                :label="currentUser?.companyCaregiverDniRequired ?
                  `${t('caregiver.dni')} (requerido)` : t('caregiver.dni')"
                :error-messages="errors.dni"
                hide-details="auto"
              />
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-text-field
                v-model="passportNumber"
                v-bind="passportNumberAttrs"
                variant="outlined"
                :label="t('caregiver.passportNumber')"
                :error-messages="errors.passportNumber"
                hide-details="auto"
              />
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-text-field
                v-model="dateOfBirth"
                type="date"
                v-bind="dateOfBirthAttrs"
                variant="outlined"
                :label="t('caregiver.dateOfBirth')"
                :error-messages="errors.dateOfBirth"
                hide-details="auto"
                min="1900-01-01"
                :max="new Date().toISOString().split('T')[0]"
              />
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-autocomplete
                v-model="countryId"
                v-bind="countryIdAttrs"
                variant="outlined"
                item-title="name"
                item-value="id"
                :label="`${t('caregiver.countryId')}`"
                :error-messages="errors.countryId"
                :items="countries"
                hide-details="auto"
              />
            </v-col>
            <v-col
              cols="12"
              sm="6"
              class="d-flex align-center"
            >
              <v-radio-group
                v-model="sex"
                v-bind="sexAttrs"
                :label="t('caregiver.sex')"
                hide-details="auto"
                inline
              >
                <v-radio
                  :label="t('caregiverForm.female')"
                  value="female"
                />
                <v-radio
                  :label="t('caregiverForm.male')"
                  value="male"
                />
              </v-radio-group>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Contact & address -->
      <v-card class="mb-6">
        <v-card-item>
          <v-card-title class="text-subtitle-1 font-weight-bold">
            {{ t('caregiverForm.sectionContactInfo') }}
          </v-card-title>
        </v-card-item>
        <v-card-text class="pt-4">
          <v-row>
            <v-col
              cols="12"
              sm="6"
            >
              <v-text-field
                v-model="email"
                v-bind="emailAttrs"
                variant="outlined"
                :label="t('caregiver.email')"
                :error-messages="errors.email"
                hide-details="auto"
              />
            </v-col>
            <v-col
              v-if="currentUser?.companyAuditable"
              cols="12"
              sm="6"
            >
              <v-text-field
                v-model="corporateEmail"
                v-bind="corporateEmailAttrs"
                variant="outlined"
                :label="t('caregiver.corporateEmail')"
                :error-messages="errors.corporateEmail"
                hide-details="auto"
              />
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-text-field
                v-model="phoneNumber"
                v-bind="phoneNumberAttrs"
                variant="outlined"
                :label="t('caregiver.phoneNumber')"
                :error-messages="errors.phoneNumber"
                hide-details="auto"
              />
            </v-col>
            <v-col
              v-if="!props.caregiver?.passwordIsSet"
              cols="12"
              sm="6"
              class="d-flex align-center"
            >
              <span
                v-if="props.caregiver?.usesApp"
                class="text-body-2 text-medium-emphasis"
              >
                {{ t('caregiverForm.usesApp') }}
              </span>
              <v-checkbox
                v-else
                v-model="sendAppAccess"
                v-bind="sendAppAccessAttrs"
                color="primary"
                :label="t('caregiverForm.sendAppAccess')"
                :hint="t('caregiverForm.sendAppAccessHint')"
                :disabled="!email"
                persistent-hint
              />
            </v-col>
            <v-col cols="12">
              <v-text-field
                id="caregiver-address-input"
                v-model="address"
                v-bind="addressAttrs"
                variant="outlined"
                :label="t('caregiver.address')"
                :error-messages="errors.address"
                hide-details="auto"
                @input="onAddressInput"
              >
                <template #append>
                  <v-btn
                    icon
                    :disabled="!latitude || !longitude"
                    @click="openMap"
                  >
                    <v-icon>mdi-map-marker</v-icon>
                  </v-btn>
                </template>
              </v-text-field>
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-autocomplete
                v-model="communeId"
                v-bind="communeIdAttrs"
                variant="outlined"
                item-title="name"
                item-value="id"
                :label="t('caregiver.communeId')"
                :error-messages="errors.communeId"
                :items="communes"
                hide-details="auto"
              />
            </v-col>
          </v-row>
          <v-dialog
            v-model="showMap"
            max-width="800px"
          >
            <v-card class="pa-2">
              <v-card-title>{{ t('caregiverForm.addressMap') }}</v-card-title>
              <v-card-text>
                <div
                  id="caregiver-map"
                  class="caregiver-map"
                />
              </v-card-text>
              <v-card-actions>
                <v-spacer />
                <v-btn
                  color="primary"
                  @click="showMap = false"
                >
                  {{ t('caregiverForm.confirm') }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-card-text>
      </v-card>

      <!-- Professional information -->
      <v-card class="mb-6">
        <v-card-item>
          <v-card-title class="text-subtitle-1 font-weight-bold">
            {{ t('caregiverForm.sectionProfessionalInfo') }}
          </v-card-title>
        </v-card-item>
        <v-card-text class="pt-4">
          <v-row>
            <v-col
              cols="12"
              sm="6"
            >
              <v-select
                v-model="career"
                v-bind="careerAttrs"
                :disabled="identityLocked"
                variant="outlined"
                :label="t('caregiver.career')"
                :error-messages="errors.career"
                :items="accessibleCareers"
                hide-details="auto"
              />
            </v-col>
            <v-col
              v-if="carePlanningEnabled && career && careerSkills && careerSkills.length > 0"
              cols="12"
            >
              <v-autocomplete
                v-model="skillIds"
                variant="outlined"
                item-title="name"
                item-value="id"
                :label="t('caregiver.skills')"
                :items="careerSkills"
                multiple
                chips
                closable-chips
                :hint="t('caregiverForm.skillsHint')"
                persistent-hint
              />
            </v-col>
            <v-col cols="12">
              <v-textarea
                v-model="description"
                v-bind="descriptionAttrs"
                variant="outlined"
                :label="t('caregiver.description')"
                :error-messages="errors.description"
                hide-details="auto"
              />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Administrative (care period + contract + groups + observaciones) -->
      <v-card class="mb-6">
        <v-card-item>
          <v-card-title class="text-subtitle-1 font-weight-bold">
            {{ t('caregiverForm.sectionAdministrative') }}
          </v-card-title>
        </v-card-item>
        <v-card-text class="pt-4">
          <v-row>
            <v-col
              cols="12"
              sm="6"
            >
              <v-text-field
                v-model="startsOn"
                type="date"
                v-bind="startsOnAttrs"
                variant="outlined"
                :label="t('caregiver.startsOn')"
                :error-messages="errors.startsOn"
                hide-details="auto"
              />
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-text-field
                v-model="endsOn"
                type="date"
                v-bind="endsOnAttrs"
                variant="outlined"
                :label="t('caregiver.endsOn')"
                :error-messages="errors.endsOn"
                hide-details="auto"
              />
            </v-col>
            <v-col
              v-if="!currentUser?.companyAuditable"
              cols="12"
            >
              <v-radio-group
                v-model="employmentContractType"
                :label="t('caregiver.employmentContractType')"
                hide-details="auto"
                inline
              >
                <v-radio
                  :label="t('caregiverForm.employmentContractTypes.contract')"
                  value="contract"
                />
                <v-radio
                  :label="t('caregiverForm.employmentContractTypes.taxBill')"
                  value="tax_bill"
                />
                <v-radio
                  :label="t('caregiverForm.employmentContractTypes.withoutContract')"
                  value="without_contract"
                />
              </v-radio-group>
            </v-col>
          </v-row>
          <group-membership-picker
            v-if="showGroupPicker"
            v-model="selectedGroupIds"
            class="mb-4"
            :required="mustPickGroup"
            :items="groupItems"
            :hint="groupPickerHint"
            :error-message="groupPickError"
          />
          <v-textarea
            v-model="comments"
            v-bind="commentsAttrs"
            variant="outlined"
            :label="t('caregiver.comments')"
            :error-messages="errors.comments"
            hide-details="auto"
            class="mt-4"
          />
        </v-card-text>
      </v-card>

      <caregiver-auditable-fields
        v-if="currentUser?.companyAuditable"
        v-model:employment-contract-type="employmentContractType"
        v-model:workday-type="workdayType"
        v-model:agreed-overtime-compensation="agreedOvertimeCompensation"
        v-model:flexible-working-hours="flexibleWorkingHours"
        v-model:exceptional-workday-resolution="exceptionalWorkdayResolution"
        v-model:exceptional-workday-date="exceptionalWorkdayDate"
        v-model:exceptional-workday-text="exceptionalWorkdayText"
        v-model:office-id="officeId"
        v-model:service-company-id="serviceCompanyId"
        v-model:compensation-type="compensationType"
        :compensation-type-errors="errors.compensationType"
      />

      <v-alert
        v-if="isCreateError && createError"
        class="mb-4 rounded"
        type="error"
      >
        {{ errorMessage(createError) }}
      </v-alert>
      <v-alert
        v-if="isUpdateError && updateError"
        class="mb-4 rounded"
        type="error"
      >
        {{ errorMessage(updateError) }}
      </v-alert>

      <!-- Sticky action bar: keeps Save reachable on a long form -->
      <div class="caregiver-form-actions d-flex align-center ga-3 py-3">
        <v-btn
          color="primary"
          size="large"
          type="submit"
          :disabled="submitButtonDisabled"
          :loading="submitButtonDisabled"
        >
          {{ mode === 'new' ? t('caregiverForm.createSubmit') : t('caregiverForm.updateSubmit') }}
        </v-btn>
      </div>
    </v-form>
  </v-container>
</template>

<style scoped>
.caregiver-map {
  width: 100%;
  height: 400px;
}

.caregiver-form-actions {
  position: sticky;
  bottom: 0;
  z-index: 5;
  background: rgb(var(--v-theme-surface));
  border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}
</style>
